import type { Spread } from 'lexical'
import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type NodeKey,
  type SerializedTextNode,
  TextNode,
} from 'lexical'

export type SerializedMentionNode = Spread<
  {
    mentionName: string
    type: 'mention'
    version: 1
  },
  SerializedTextNode
>

function convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const name = domNode.textContent
  const employeeId = parseInt(
    domNode?.querySelector('a')?.getAttribute('href')?.split('/')[2] || '0'
  )

  if (name !== null && employeeId !== 0) {
    const node = $createMentionNode({
      name,
      employeeId,
    })
    return {
      node,
    }
  }

  return null
}
export class MentionNode extends TextNode {
  __mention: string
  __id: number
  __employeeId: number

  static getType(): string {
    return 'mention'
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      { name: node.__mention, id: node.__id, employeeId: node.__employeeId },
      node.__text,
      node.__key
    )
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode(serializedNode.mentionName)
    node.setTextContent(serializedNode.text)
    node.setFormat(serializedNode.format)
    node.setDetail(serializedNode.detail)
    node.setMode(serializedNode.mode)
    node.setStyle(serializedNode.style)
    return node
  }

  constructor(mention: any, text?: string, key?: NodeKey) {
    const userName = mention.name ? mention.name : mention
    super(text ?? userName, key)
    this.__mention = userName
    this.__id = mention.id || 0
    this.__employeeId = mention.employeeId
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionName: this.__mention,
      type: 'mention',
      version: 1,
    }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config)
    dom.className = 'mention'
    dom.setAttribute('data-lexical-id', this.__id.toString())
    return dom
  }

  exportDOM(): DOMExportOutput {
    const element = window.document.createElement('strong')
    element.setAttribute('data-lexical-mention', 'true')
    element.className = 'mentioned'

    const employeeId = this.__employeeId

    if (employeeId) {
      const link = window.document.createElement('a')
      link.className = 'link_mentioned'
      link.href = `/employees/${employeeId}`
      link.innerHTML = element.outerHTML
      link.textContent = this.__text

      element.appendChild(link)
    } else {
      element.textContent = this.__text
    }

    return { element }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      strong: (domNode: HTMLElement) => {
        if (domNode.className !== 'mentioned') {
          return null
        }
        return {
          conversion: convertMentionElement,
          priority: 1,
        }
      },
    }
  }

  isTextEntity(): true {
    return true
  }
}

export function $createMentionNode(mention: any): MentionNode {
  const mentionNode = new MentionNode(mention)
  mentionNode.setMode('segmented').toggleDirectionless()
  return $applyNodeReplacement(mentionNode)
}
